import { startOfDay, areIntervalsOverlapping } from 'date-fns';
import { groupBy, sortBy, orderBy, values } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RosterDay } from '../../types';
import {
  Absence,
  Leave as LeaveType,
  Illness as IllnessType,
  UnspecifiedAbsence as UnspecifiedType
} from '../types';
import { Interval } from 'components/types';

import { GRAY } from 'constants/colors';

import { getCellDateClasses } from '../../utils';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { MARGIN, PADDING, getAbsenceCoords } from './Absence';
import Illness from './Illness';
import Leave from './Leave';
import Unspecified from './UnspecifiedAbsence';

const StyledTableRow = styled(TableRow)`
  .day-columns {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    border-left: 1px dashed ${GRAY};
    box-sizing: border-box;

    .day {
      position: relative;
      height: 100%;
      flex-grow: 1;

      &:not(:last-child) {
        border-right: 1px dashed ${GRAY};
      }
    }
  }

  .by-employee {
    display: flex;
    margin: ${MARGIN}px 0;
    position: relative;
    z-index: 1;
  }

  .by-startdate {
    padding: 0 ${PADDING}px;
    box-sizing: border-box;
    position: relative;
    z-index: 1;

    .absence {
      margin-bottom: ${MARGIN}px;
    }

    .display-contents:last-child {
      .absence {
        margin-bottom: 0;
      }
    }
  }
`;

type Props = {
  type: 'leave' | 'illness' | 'unspecified';
  absences: Absence[];
};

type AbsencesByStartDateProps = {
  prevAbsence: Absence | null;
  interval: Interval;
} & Props;

type AbsencesByTypeProps = {
  rosterDays: RosterDay[];
  interval: Interval;
} & Props;

// overlaping illness of internal & external absences will be "visually merged" into one illness absence, eg:
// <---- internal ---------- external --- internal ------>

const AbsencesByStartDate = ({
  absences,
  prevAbsence,
  type,
  interval
}: AbsencesByStartDateProps) => {
  // internal illness absences for the same day will be merged into one absence;
  if (type === 'illness') {
    const internalIllness = (absences as IllnessType[]).filter(
      (absence) => !absence.isExternal
    );
    const internalIllnessAbsence = internalIllness.reduce(
      (previousIllness, currentIllness, i, arr) => ({
        ...currentIllness,
        dutyHourType: {
          ...previousIllness.dutyHourType,
          description: Array.from(
            new Set(arr.map(({ dutyHourType }) => dutyHourType.description))
          ).join(', ')
        },
        start: orderBy(arr, (absence) => new Date(absence.start), 'asc')[0]
          .start,
        end: orderBy(arr, (absence) => new Date(absence.end), 'desc')[0].end
      }),
      internalIllness[0]
    );
    const externalIllness = (absences as IllnessType[]).filter(
      (absence) => absence.isExternal
    );
    absences = [
      ...(internalIllnessAbsence ? [internalIllnessAbsence] : []),
      ...externalIllness
    ];
  }

  const absence = absences[0];
  // eslint-disable-next-line prefer-const
  let { marginLeft, width } = getAbsenceCoords(absence, interval);

  if (prevAbsence) {
    const { marginLeft: mLPrevAbsence, width: wPrevAbsence } = getAbsenceCoords(
      prevAbsence,
      interval
    );
    marginLeft = marginLeft - mLPrevAbsence - wPrevAbsence;
  }

  return (
    <div
      className="by-startdate"
      style={{
        marginLeft: `${marginLeft}%`,
        width: `${width}%`
      }}
    >
      {absences.map((absence) => {
        if (type === 'leave') {
          return (
            <Leave
              key={absence.id}
              absence={absence as LeaveType}
              interval={interval}
            />
          );
        }
        if (type === 'illness') {
          return (
            <Illness
              key={absence.id}
              absence={absence as IllnessType}
              interval={interval}
            />
          );
        }
        return (
          <Unspecified
            key={absence.id}
            absence={absence as UnspecifiedType}
            interval={interval}
          />
        );
      })}
    </div>
  );
};

export default function AbsencesByType({
  absences,
  type,
  rosterDays,
  interval
}: AbsencesByTypeProps) {
  const { t } = useTranslation('common');

  return (
    <StyledTableRow>
      <TableCell className="first-cell">
        <h3>{t(type === 'unspecified' ? 'absent' : type)}</h3>
      </TableCell>
      <TableCell colSpan={rosterDays.length}>
        <div className="day-columns">
          {rosterDays.map(({ date, dayNr }) => (
            <div
              key={dayNr}
              className={getCellDateClasses({ date }, ['day'])}
            />
          ))}
        </div>
        {sortBy(
          values(
            groupBy(absences, (absence) => [
              absence.employee.id,
              absence.workAgreementId
            ])
          ),
          (absences) => absences[0].employee.nameInformal
        ).map((absencesByEmployee) => (
          <div
            className="by-employee"
            key={absencesByEmployee.map(({ id }) => id).join()}
          >
            {sortBy(
              values(
                groupBy(
                  absencesByEmployee.map((absence) =>
                    type === 'illness'
                      ? {
                          ...absence,
                          hasOverlap:
                            absencesByEmployee.length > 1 &&
                            absencesByEmployee.some(({ start, end }) =>
                              areIntervalsOverlapping(
                                { start: new Date(start), end: new Date(end) },
                                {
                                  start: new Date(absence.start),
                                  end: new Date(absence.end)
                                }
                              )
                            )
                        }
                      : absence
                  ),
                  (absence) => [
                    startOfDay(new Date(absence.start)),
                    absence.type === 'ILLNESS' ? absence.isExternal : ''
                  ]
                )
              ),
              (absences) => [
                absences[0].type === 'ILLNESS' ? !absences[0].isExternal : '',
                absences[0].start
              ]
            ).map((absencesByStartDate, i, arr) => (
              <AbsencesByStartDate
                key={absencesByStartDate.map(({ id }) => id).join()}
                absences={sortBy(absencesByStartDate, (absence) =>
                  startOfDay(new Date(absence.start))
                )}
                prevAbsence={i > 0 ? arr[i - 1][0] : null}
                type={type}
                interval={interval}
              />
            ))}
          </div>
        ))}
      </TableCell>
    </StyledTableRow>
  );
}
